import { addDays, format, getISODay, isAfter, parse, parseISO, startOfDay } from 'date-fns';
import { ProfessionalAvailability } from '../../types/AvailabilityModels';
import { Topic } from '../../types/TopicModels';

export interface NewSlot {
  dateIso: string; // yyyy-MM-dd
  from: string;    // HH:mm
  to: string;      // HH:mm
  rate: number;
  topicId: number;
}

export const minutesToHHmm = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

export const toMinutes = (time: string): number => {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
};

export const expandRecurringDates = (days: Set<number>, endDateStr: string): string[] => {
  if (days.size === 0) return [];
  const end = parse(endDateStr, 'dd MMM yyyy', new Date());
  let current = startOfDay(new Date());
  const dates: string[] = [];
  while (!isAfter(current, end)) {
    if (days.has(getISODay(current))) {
      dates.push(format(current, 'yyyy-MM-dd'));
    }
    current = addDays(current, 1);
  }
  return dates;
};

export const buildNewSlots = (
  topics: Topic[],
  startTime: string,
  endTime: string,
  dates: string[]
): NewSlot[] => {
  const startMinutes = toMinutes(startTime);
  const endMinutes = toMinutes(endTime);
  const slots: NewSlot[] = [];

  for (const dateIso of dates) {
    for (const topic of topics) {
      const duration = topic.durationMinutes ?? 30;
      let cursor = startMinutes;
      while (cursor + duration <= endMinutes) {
        slots.push({
          dateIso,
          from: minutesToHHmm(cursor),
          to: minutesToHHmm(cursor + duration),
          rate: topic.rate ?? 0,
          topicId: topic.id,
        });
        cursor += duration;
      }
    }
  }

  return slots;
};

/**
 * Returns true if newSlot overlaps with any existing slot for the same topic on the same date.
 * Different topics are allowed to overlap — only same-topic conflicts are blocked.
 */
export const hasTopicConflict = (
  slot: NewSlot,
  existingAvailability: ProfessionalAvailability | null
): boolean => {
  if (!existingAvailability) return false;

  const newFrom = toMinutes(slot.from);
  const newTo = toMinutes(slot.to);

  for (const availableDate of existingAvailability.availableDates) {
    const existingDateIso = format(
      parse(availableDate.date, 'MM/dd/yyyy', new Date()),
      'yyyy-MM-dd'
    );
    if (existingDateIso !== slot.dateIso) continue;

    for (const time of availableDate.availableTimes) {
      if (!time.topics?.some((t) => t.id === slot.topicId)) continue;

      const existingFromMinutes = toMinutes(format(parseISO(time.startIso), 'HH:mm'));
      const existingToMinutes = toMinutes(format(parseISO(time.endIso), 'HH:mm'));

      // Overlap: newFrom < existingTo AND existingFrom < newTo
      if (newFrom < existingToMinutes && existingFromMinutes < newTo) {
        return true;
      }
    }
  }

  return false;
};

export const filterConflictingSlots = (
  newSlots: NewSlot[],
  existingAvailability: ProfessionalAvailability | null
): { validSlots: NewSlot[]; conflictCount: number } => {
  const validSlots = newSlots.filter((slot) => !hasTopicConflict(slot, existingAvailability));
  return { validSlots, conflictCount: newSlots.length - validSlots.length };
};

export const buildPayload = (slots: NewSlot[]) => {
  const grouped = slots.reduce<Record<string, NewSlot[]>>((acc, slot) => {
    if (!acc[slot.dateIso]) acc[slot.dateIso] = [];
    acc[slot.dateIso].push(slot);
    return acc;
  }, {});

  return {
    professionalTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    availableDates: Object.entries(grouped).map(([date, dateSlots]) => ({
      date,
      available: true,
      currency: 'GBP',
      availableTimes: dateSlots.map((slot) => ({
        from: slot.from,
        to: slot.to,
        rate: slot.rate,
        topicIds: [slot.topicId],
      })),
    })),
  };
};
